import React, { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Upload, X, FileText, Package } from "@phosphor-icons/react";
import { toast } from "sonner";
import { currencySymbol, formatCurrency } from "@/lib/currency";
import { Field } from "./shared";

export const BopBidderView = ({ a, onChange }) => {
  const lineItems = a.line_items || [];
  const myPrev = a.my_current_round_bid?.line_prices || {};

  const [prices, setPrices] = useState(() => {
    const init = {};
    lineItems.forEach((li) => { init[li.item_number] = myPrev[li.item_number] != null ? String(myPrev[li.item_number]) : ""; });
    return init;
  });
  const [meta, setMeta] = useState({
    warranty: a.my_current_round_bid?.warranty || "",
    remarks: a.my_current_round_bid?.remarks || "",
  });
  const [att, setAtt] = useState(a.my_current_round_bid ? {
    storage_path: a.my_current_round_bid.attachment_path,
    original_filename: a.my_current_round_bid.attachment_filename,
  } : null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Live total
  const total = useMemo(() => {
    let t = 0;
    for (const li of lineItems) {
      const p = parseFloat(prices[li.item_number]);
      if (!Number.isFinite(p) || p < 0) return null;
      t += p * Number(li.forecast_qty || 0);
    }
    return Math.round(t * 100) / 100;
  }, [prices, lineItems]);

  const allFilled = lineItems.every((li) => prices[li.item_number] !== "" && parseFloat(prices[li.item_number]) >= 0);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAtt(data);
      toast.success("Attached");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!allFilled) {
      toast.error("Quote every line item before submitting");
      return;
    }
    setBusy(true);
    try {
      const line_prices = {};
      lineItems.forEach((li) => { line_prices[li.item_number] = Number(prices[li.item_number]); });
      await api.post(`/auctions/${a.id}/bids`, {
        line_prices,
        warranty: meta.warranty,
        remarks: meta.remarks,
        attachment_path: att?.storage_path || null,
        attachment_filename: att?.original_filename || null,
      });
      toast.success("BOP bid submitted");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const sym = currencySymbol(a.currency);

  return (
    <div className="space-y-6" data-testid="bop-bidder-view">
      {/* Summary card */}
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-[#F8F9FA] border border-black/10 p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2 flex items-center gap-2">
            <Package size={12} weight="bold" /> Line-Item Quote · Round {a.current_round}
          </div>
          <div className="font-display font-extrabold text-3xl lg:text-4xl tracking-tightest leading-none">
            {lineItems.length} item{lineItems.length === 1 ? "" : "s"} · Total
          </div>
          <div className="mt-4 font-mono-plex text-4xl klein-blue" data-testid="bop-bid-total">
            {total != null ? formatCurrency(total, a.currency) : "—"}
          </div>
          <p className="text-xs text-neutral-600 mt-3 leading-relaxed">
            Evaluation is on the <strong>total bid value</strong> (sum of price × forecast qty across all items). Lowest total wins. You will never see other bidders&apos; prices.
          </p>
        </div>
        <div className="lg:col-span-2 border border-black/10 p-6">
          {a.current_round > 1 && (a.rounds?.[a.current_round - 2]?.line_targets || a.rounds?.[a.current_round - 2]?.target_price != null) && (
            <>
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">Creator&apos;s Target</div>
              {a.rounds[a.current_round - 2].line_targets ? (
                <>
                  <div className="font-mono-plex text-xl klein-blue" data-testid="bop-target-display">
                    per item (see table below)
                  </div>
                  <p className="text-xs text-neutral-600 mt-2 leading-relaxed">
                    Targets are now set <strong>per line</strong>. Quote each item at or below its target unit price.
                  </p>
                </>
              ) : (
                <>
                  <div className="font-mono-plex text-3xl klein-blue" data-testid="bop-target-display">
                    {formatCurrency(a.rounds[a.current_round - 2].target_price, a.currency)}
                  </div>
                  <p className="text-xs text-neutral-600 mt-2 leading-relaxed">
                    Quote so your total comes in at or below this target.
                  </p>
                </>
              )}
            </>
          )}
          {a.my_current_round_bid && (
            <div className="border-t border-black/10 mt-4 pt-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">Your Submitted Total</div>
              <div className="font-mono-plex text-2xl" data-testid="bop-my-submitted-total">
                {formatCurrency(a.my_current_round_bid.price, a.currency)}
              </div>
              <div className="text-xs text-neutral-500 mt-1">You can update any price below — your new total replaces the old one.</div>
            </div>
          )}
        </div>
      </div>

      {/* Bid form */}
      <form onSubmit={submit} className="border border-black/10 p-6 space-y-6" data-testid="bop-bid-form">
        <div>
          <div className="border-b border-black pb-2 mb-3 flex items-baseline justify-between">
            <h3 className="font-display font-extrabold text-xl tracking-tightest">Line Item Prices</h3>
            <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{sym} per unit</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bop-line-table">
              <thead>
                <tr className="border-b border-black/10 text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                  <th className="text-left p-2 w-10">#</th>
                  <th className="text-left p-2">Item</th>
                  <th className="text-left p-2 w-20">UOM</th>
                  <th className="text-right p-2 w-32">Forecast Qty</th>
                  <th className="text-right p-2 w-32">Target ({sym})</th>
                  <th className="text-right p-2 w-40">Unit Price ({sym}) *</th>
                  <th className="text-right p-2 w-40">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => {
                  const p = parseFloat(prices[li.item_number]);
                  const lineTotal = Number.isFinite(p) ? p * Number(li.forecast_qty || 0) : null;
                  const targetUnit = a.current_round > 1
                    ? a.rounds?.[a.current_round - 2]?.line_targets?.[li.item_number]
                    : null;
                  const overTarget = targetUnit != null && Number.isFinite(p) && p > targetUnit;
                  return (
                    <tr key={li.item_number} className="border-b border-black/5" data-testid={`bop-line-${li.item_number}`}>
                      <td className="p-2 font-mono-plex text-neutral-500">{i + 1}</td>
                      <td className="p-2">
                        <div className="font-mono-plex font-semibold">{li.item_number}</div>
                        <div className="text-xs text-neutral-600">{li.item_desc}</div>
                      </td>
                      <td className="p-2 text-xs">{li.uom || "—"}</td>
                      <td className="p-2 text-right font-mono-plex">{Number(li.forecast_qty).toLocaleString()}</td>
                      <td className="p-2 text-right font-mono-plex text-xs">
                        {targetUnit != null ? <span className="klein-blue font-semibold">{formatCurrency(targetUnit, a.currency)}</span> : "—"}
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="0"
                          step="any"
                          required
                          value={prices[li.item_number]}
                          onChange={(e) => setPrices({ ...prices, [li.item_number]: e.target.value })}
                          placeholder="0.00"
                          className={`rounded-none h-9 text-right font-mono-plex ${overTarget ? "border-red-500 text-red-700" : "border-black/20"}`}
                          data-testid={`bop-line-price-${li.item_number}`}
                          title={overTarget ? "Above the round target — bidders typically need to be at or below" : ""}
                        />
                      </td>
                      <td className="p-2 text-right font-mono-plex">
                        {lineTotal != null ? formatCurrency(lineTotal, a.currency) : "—"}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-black font-semibold" data-testid="bop-total-row">
                  <td colSpan={6} className="p-2 text-right text-[10px] tracking-[0.25em] uppercase text-neutral-500">Total Bid Value</td>
                  <td className="p-2 text-right font-mono-plex text-lg klein-blue">
                    {total != null ? formatCurrency(total, a.currency) : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-black/10 pt-6">
          <Field label="Warranty / Validity" required>
            <Input value={meta.warranty} onChange={(e) => setMeta({ ...meta, warranty: e.target.value })} required placeholder="e.g. 2 years" className="rounded-none border-black/20 h-11" data-testid="bop-warranty" />
          </Field>
          <Field label="Remarks">
            <Textarea value={meta.remarks} onChange={(e) => setMeta({ ...meta, remarks: e.target.value })} rows={3} className="rounded-none border-black/20" data-testid="bop-remarks" />
          </Field>
        </div>

        <div className="border-t border-black/10 pt-6 space-y-2">
          <div className="text-[10px] tracking-[0.25em] uppercase font-semibold">Attachment (optional)</div>
          {!att ? (
            <label className="border border-dashed border-black/20 p-3 flex items-center gap-3 cursor-pointer hover:border-black" data-testid="bop-upload-zone">
              <Upload size={16} weight="bold" />
              <span className="text-sm">{uploading ? "Uploading..." : "Click to upload"}</span>
              <input type="file" hidden onChange={onUpload} disabled={uploading} />
            </label>
          ) : (
            <div className="border border-black/15 p-3 flex items-center gap-3" data-testid="bop-attachment">
              <FileText size={16} weight="bold" />
              <div className="flex-1 text-sm truncate">{att.original_filename}</div>
              <button type="button" onClick={() => setAtt(null)}><X size={14} weight="bold" /></button>
            </div>
          )}
        </div>

        <Button type="submit" disabled={busy || !allFilled} className="w-full rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-12 btn-sharp" data-testid="bop-submit-bid">
          {busy ? "Submitting..." : (a.my_current_round_bid ? "Update BOP Bid" : "Submit BOP Bid")}
          <ArrowRight size={14} weight="bold" className="ml-2" />
        </Button>
        <p className="text-xs text-neutral-500 leading-relaxed">
          <strong>Tip:</strong> ties on the lowest total go to whoever submitted first. Quote your best total early.
        </p>
      </form>
    </div>
  );
};
